import { zodResolver } from "@hookform/resolvers/zod";
import { Turnstile } from "@marsidev/react-turnstile";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { forgotPasswordRoute } from "@/routes/forgotPasswordRoute";
import { authClient } from "../../lib/auth";
import { Field, FieldError, FieldGroup, FieldLabel } from "../ui/field";

// Zod validation schema
const forgotPasswordSchema = z.object({
    email: z.string().email("Please enter a valid email address").min(1, "Email is required"),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordForm({ className, ...props }: React.ComponentProps<"div">) {
    const [token, setToken] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const { requested } = useSearch({ from: "/forgot-password" }) as {
        requested: string | undefined;
    };
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        setError,
        formState: { errors },
    } = useForm<ForgotPasswordFormData>({
        resolver: zodResolver(forgotPasswordSchema),
        mode: "onBlur",
    });

    const onSubmit = async (values: ForgotPasswordFormData) => {
        setIsLoading(true);
        const { data, error } = await authClient.requestPasswordReset({
            email: values.email,
            redirectTo: `${window.location.origin}/reset-password`,
            fetchOptions: {
                headers: {
                    "x-captcha-response": token,
                },
            },
        });

        if (error) {
            console.error("Request error:", error);
            setError("email", { message: error.message });
        } else {
            console.log("Request successful:", data);
            navigate({
                to: forgotPasswordRoute.to,
                search: { requested: "true" },
            });
        }
        setIsLoading(false);
    };

    if (requested) {
        return (
            <Card>
                <CardHeader className="text-center">
                    <CardTitle className="text-xl">Forgot Password</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="mb-6">
                        Please check yout email. If an account for the email address provided exists, you will receive a reset password email.
                    </div>
                    <div>
                        <Button
                            variant="default"
                            nativeButton={false}
                            className="w-full text-base"
                            render={
                                <Link to="/login">
                                    <ArrowLeftIcon />
                                    Back to login
                                </Link>
                            }
                        ></Button>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className={cn("flex flex-col gap-6", className)} {...props}>
            <Card>
                <CardHeader className="text-center">
                    <CardTitle className="text-xl">Forgot Password</CardTitle>
                    <CardDescription>Enter your email address to reset your password</CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit(onSubmit)} id="loginForm">
                        <div className="grid gap-6">
                            <div className="grid gap-6">
                                <FieldGroup className="gap-4">
                                    <Field>
                                        <FieldLabel htmlFor="email">Email</FieldLabel>
                                        <Input
                                            type="email"
                                            placeholder="m@example.com"
                                            {...register("email")}
                                            className={errors.email ? "border-red-500" : ""}
                                        />
                                        <FieldError errors={[errors.email]} />
                                    </Field>
                                </FieldGroup>

                                <Turnstile
                                    onSuccess={setToken}
                                    className="w-full mx-auto"
                                    options={{
                                        theme: "light",
                                        size: "flexible",
                                    }}
                                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY!}
                                />
                                <Button type="submit" form="loginForm" className="w-full [&_iframe]:mx-auto text-base" disabled={isLoading || !token}>
                                    {isLoading ? "Sending reset email..." : "Request new password"}
                                </Button>
                            </div>
                            <div className="text-center ">
                                Remember your password?{" "}
                                <a href="/login" className="underline underline-offset-4">
                                    Login
                                </a>
                            </div>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
